import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Plus, Search, Upload } from 'lucide-react';
import useClasses from './hooks/useClasses';
import { useSchools } from './hooks/useSchools';
import { CLASS_STATUSES, SCHOOL_YEARS, normalizeSchoolYear } from './constants';
import type { Class, ClassFilters, ClassCreateRequest } from './types';
import CreateClassModal from './components/CreateClassModal';
import EditClassModal from './components/EditClassModal';
import ImportClassModal from './components/ImportClassModal';
import AdminListSortSelect from '../../components/ui/AdminListSortSelect';

// ClassDashboard component with school column
const ClassDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [editingClass, setEditingClass] = useState<Class | null>(null);

  const {
    classes,
    pagination,
    filters,
    isLoading,
    error,
    updateFilters,
    updatePagination,
    resetFilters,
    refetch,
    deleteClass,
    toggleStatus,
    isDeleting,
    isTogglingStatus,
    createClass,
    isCreating,
    createClassMutation,
    deleteClassMutation,
    toggleStatusMutation,
    page,
    per_page
  } = useClasses();

  // Fetch schools for the filter dropdown
  const { data: allSchools = [], isLoading: isLoadingSchools } = useSchools({
    per_page: 1000, // Get all schools for the dropdown
    enabled: true
  });

  const handleFilterChange = (key: keyof ClassFilters, value: string) => {
    console.log('Filter change:', { key, value, currentFilters: filters });
    updateFilters({ [key]: value });
  };

  const handlePageChange = (page: number) => {
    updatePagination(page);
  };

  const handlePerPageChange = (perPage: number) => {
    updatePagination(1, perPage);
  };

  const handleCreateClass = () => {
    setIsCreateModalOpen(true);
  };

  const handleImportClasses = () => {
    setIsImportModalOpen(true);
  };

  const handleCreateClassSubmit = async (data: ClassCreateRequest) => {
    try {
      await createClassMutation.mutateAsync(data);
      setIsCreateModalOpen(false);
      toast.success(`Tạo lớp học "${data.name}" thành công!`);
      refetch(); // Refresh the list after creating
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Có lỗi xảy ra khi tạo lớp học');
      throw error; // Re-throw to let modal handle it
    }
  };

  const handleEditClass = (classItem: Class) => {
    setEditingClass(classItem);
  };

  const handleViewClass = (classId: string) => {
    navigate(`/admin/class/${classId}`);
  };

  const handleDeleteClass = (classId: string, className: string) => {
    toast((t) => (
      <div className="flex flex-col space-y-3">
        <div className="flex items-center space-x-2">
          <span className="text-lg">⚠️</span>
          <span className="font-medium">Xác nhận xóa lớp học</span>
        </div>
        <p className="text-sm text-gray-600">
          Bạn có chắc chắn muốn xóa lớp học <strong>"{className}"</strong>?
        </p>
        <div className="flex space-x-2 justify-end">
          <button
            onClick={() => {
              toast.dismiss(t.id);
            }}
            className="px-3 py-1 text-sm bg-gray-200 hover:bg-gray-300 rounded transition-colors"
          >
            Hủy
          </button>
          <button
            onClick={() => {
              toast.dismiss(t.id);
              toast.promise(
                deleteClassMutation.mutateAsync(classId),
                {
                  loading: 'Đang xóa lớp học...',
                  success: `Xóa lớp học "${className}" thành công!`,
                  error: (err) => err?.response?.data?.message || 'Có lỗi xảy ra khi xóa lớp học',
                }
              );
            }}
            className="px-3 py-1 text-sm bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
          >
            Xóa
          </button>
        </div>
      </div>
    ), {
      duration: Infinity,
      style: {
        background: '#fff',
        color: '#000',
        maxWidth: '400px',
      },
    });
  };

  const handleToggleStatus = (classId: string, className: string, currentStatus: string) => {
    const actionText = currentStatus === CLASS_STATUSES.ACTIVE ? 'tạm dừng' : 'kích hoạt';
    toast((t) => (
      <div className="flex flex-col space-y-3">
        <div className="flex items-center space-x-2">
          <span className="text-lg">{currentStatus === CLASS_STATUSES.ACTIVE ? '⏸️' : '▶️'}</span>
          <span className="font-medium">Xác nhận {actionText} lớp học</span>
        </div>
        <p className="text-sm text-gray-600">
          Bạn có chắc chắn muốn {actionText} lớp học <strong>"{className}"</strong>?
        </p>
        <div className="flex space-x-2 justify-end">
          <button
            onClick={() => {
              toast.dismiss(t.id);
            }}
            className="px-3 py-1 text-sm bg-gray-200 hover:bg-gray-300 rounded transition-colors"
          >
            Hủy
          </button>
          <button
            onClick={() => {
              toast.dismiss(t.id);
              toast.promise(
                toggleStatusMutation.mutateAsync(classId),
                {
                  loading: `Đang ${actionText} lớp học...`,
                  success: `${actionText.charAt(0).toUpperCase() + actionText.slice(1)} lớp học "${className}" thành công!`,
                  error: (err) => err?.response?.data?.message || `Có lỗi xảy ra khi ${actionText} lớp học`,
                }
              );
            }}
            className={`px-3 py-1 text-sm text-white rounded transition-colors ${
              currentStatus === CLASS_STATUSES.ACTIVE 
                ? 'bg-orange-600 hover:bg-orange-700' 
                : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {actionText.charAt(0).toUpperCase() + actionText.slice(1)}
          </button>
        </div>
      </div>
    ), {
      duration: Infinity,
      style: {
        background: '#fff',
        color: '#000',
        maxWidth: '400px',
      },
    });
  };

  const handleImportSuccess = () => {
    refetch(); // Refresh the list after importing
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, string> = {
      [CLASS_STATUSES.ACTIVE]: 'bg-green-100 text-green-800',
      [CLASS_STATUSES.INACTIVE]: 'bg-gray-100 text-gray-800',
      [CLASS_STATUSES.ARCHIVED]: 'bg-blue-100 text-blue-800'
    };
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusConfig[status] || 'bg-gray-100 text-gray-800'}`}>
        {status === CLASS_STATUSES.ACTIVE && 'Hoạt động'}
        {status === CLASS_STATUSES.INACTIVE && 'Không hoạt động'}
        {status === CLASS_STATUSES.ARCHIVED && 'Đã lưu trữ'}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Quản lý Lớp học</h1>
          <p className="text-gray-600 mt-1">
            Quản lý danh sách các lớp học, thông tin và hiệu suất đào tạo
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleCreateClass}
            disabled={isCreating}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            {isCreating ? 'Đang tạo...' : 'Thêm lớp học'}
          </button>
          <button 
            onClick={handleImportClasses}
            disabled={isCreating}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import từ Excel
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Tìm kiếm theo tên lớp, trường học hoặc năm học..."
                value={filters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <select 
              value={filters.schoolId || 'all'} 
              onChange={(e) => {
                const value = e.target.value === 'all' ? '' : e.target.value;
                console.log('School select changed:', { value, isAll: e.target.value === 'all' });
                handleFilterChange('schoolId', value);
              }}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isLoadingSchools}
            >
              <option value="all">
                {isLoadingSchools ? 'Đang tải trường học...' : 'Tất cả trường học'}
              </option>
              {allSchools.map((school) => (
                <option key={school.id} value={school.id}>{school.name}</option>
              ))}
            </select>
            <select 
              value={filters.schoolYear || 'all'} 
              onChange={(e) => handleFilterChange('schoolYear', e.target.value === 'all' ? '' : e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tất cả năm học</option>
              {SCHOOL_YEARS.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            <AdminListSortSelect
              value={filters.sort}
              onChange={(value) => handleFilterChange('sort', value)}
              disabled={isLoading}
            />
            <button
              onClick={() => {
                resetFilters();
                toast.success('Đã reset bộ lọc', {
                  icon: '🔄',
                  duration: 2000,
                });
              }}
              className="px-3 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="space-y-6">
          {/* Header Skeleton */}
          <div className="flex justify-between items-start">
            <div>
              <div className="h-8 w-64 bg-gray-200 rounded animate-pulse mb-1"></div>
              <div className="h-4 w-80 bg-gray-200 rounded animate-pulse"></div>
            </div>
            <div className="h-10 w-40 bg-gray-200 rounded-lg animate-pulse"></div>
          </div>

          {/* Search and Filters Skeleton */}
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1">
                <div className="h-10 bg-gray-200 rounded-md animate-pulse"></div>
              </div>
              <div className="flex gap-3">
                <div className="h-10 w-40 bg-gray-200 rounded-md animate-pulse"></div>
                <div className="h-10 w-32 bg-gray-200 rounded-md animate-pulse"></div>
                <div className="h-10 w-24 bg-gray-200 rounded-md animate-pulse"></div>
              </div>
            </div>
          </div>

          {/* Classes List Skeleton */}
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="h-6 w-48 bg-gray-200 rounded animate-pulse"></div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <th key={i} className="px-6 py-3 text-left">
                        <div className="h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <tr key={i} className="animate-pulse">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-12 w-12 bg-gray-200 rounded-lg"></div>
                          <div className="ml-4">
                            <div className="h-4 w-32 bg-gray-200 rounded"></div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="h-4 w-20 bg-gray-200 rounded"></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="h-4 w-24 bg-gray-200 rounded"></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="h-4 w-20 bg-gray-200 rounded"></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="h-6 w-24 bg-gray-200 rounded-full"></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex space-x-2">
                          <div className="h-8 w-8 bg-gray-200 rounded-lg"></div>
                          <div className="h-8 w-8 bg-gray-200 rounded-lg"></div>
                          <div className="h-8 w-8 bg-gray-200 rounded-lg"></div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination Skeleton */}
            <div className="px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="h-4 w-48 bg-gray-200 rounded animate-pulse"></div>
                  <div className="flex items-center space-x-2">
                    <div className="h-4 w-16 bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-8 w-16 bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-4 w-8 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="h-8 w-16 bg-gray-200 rounded animate-pulse"></div>
                  <div className="flex space-x-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
                    ))}
                  </div>
                  <div className="h-8 w-16 bg-gray-200 rounded animate-pulse"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Classes List */}
      {!isLoading && (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">Danh sách Lớp học</h2>
            {isLoading && (
              <div className="flex items-center text-sm text-gray-500">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                Đang cập nhật...
              </div>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    TÊN LỚP
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    TRƯỜNG HỌC
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    NĂM HỌC
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    SĨ SỐ
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    TRẠNG THÁI
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    HÀNH ĐỘNG
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {error ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center space-y-4">
                        <div className="text-red-500">
                          <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div className="text-center">
                          <h3 className="text-lg font-medium text-red-600 mb-2">Lỗi tải dữ liệu</h3>
                          <p className="text-gray-600 mb-4">{error?.message || 'Đã xảy ra lỗi khi tải danh sách lớp học'}</p>
                          <button 
                            onClick={() => {
                              toast.promise(
                                refetch(),
                                {
                                  loading: 'Đang tải lại dữ liệu...',
                                  success: 'Tải dữ liệu thành công!',
                                  error: 'Không thể tải dữ liệu',
                                }
                              );
                            }}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            Thử lại
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : classes.length === 0 && !isLoading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      Không tìm thấy lớp học nào
                    </td>
                  </tr>
                ) : (
                  classes.map((classItem: Class) => (
                    <tr key={classItem.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-12 w-12">
                            <div className="h-12 w-12 rounded-lg bg-indigo-100 flex items-center justify-center">
                              <svg className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                              </svg>
                            </div>
                          </div>
                          <div className="ml-4">
                            <Link
                              to={`/admin/class/${classItem.id}`}
                              className="text-sm font-medium text-blue-600 hover:text-blue-900 hover:underline"
                            >
                              {classItem.name}
                            </Link>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{classItem.school?.name || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{normalizeSchoolYear(classItem.schoolYear)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{classItem.studentCount?.toLocaleString() || 0}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(classItem.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <Link
                            to={`/admin/class/${classItem.id}`}
                            className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Xem chi tiết"
                          >
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </Link>
                          <button
                            onClick={() => handleEditClass(classItem)}
                            className="p-2 text-green-600 hover:text-green-900 hover:bg-green-50 rounded-lg transition-colors"
                            title="Chỉnh sửa"
                          >
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleToggleStatus(classItem.id, classItem.name, classItem.status)}
                            disabled={isTogglingStatus}
                            className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${
                              classItem.status === CLASS_STATUSES.ACTIVE 
                                ? 'text-orange-600 hover:text-orange-900 hover:bg-orange-50' 
                                : 'text-green-600 hover:text-green-900 hover:bg-green-50'
                            }`}
                            title={classItem.status === CLASS_STATUSES.ACTIVE ? 'Tạm dừng hoạt động' : 'Kích hoạt hoạt động'}
                          >
                            {classItem.status === CLASS_STATUSES.ACTIVE ? (
                              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            ) : (
                              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            )}
                          </button>
                          <button
                            onClick={() => handleDeleteClass(classItem.id, classItem.name)}
                            disabled={isDeleting}
                            className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                            title="Xóa lớp học"
                          >
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          
          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="text-sm text-gray-700">
                    Hiển thị {((page - 1) * per_page) + 1} đến {Math.min(page * per_page, pagination.total)} 
                    trong tổng số {pagination.total} lớp học
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-700">Hiển thị:</span>
                    <select
                      value={per_page}
                      onChange={(e) => handlePerPageChange(Number(e.target.value))}
                      className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span className="text-sm text-gray-700">mục</span>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page <= 1}
                    className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Trước
                  </button>
                  
                  {/* Page Numbers */}
                  <div className="flex items-center space-x-1">
                    {(() => {
                      const totalPages = pagination.totalPages;
                      const currentPage = page;
                      const pages = [];
                      
                      // Always show first page
                      if (currentPage > 3) {
                        pages.push(
                          <button
                            key={1}
                            onClick={() => handlePageChange(1)}
                            className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                          >
                            1
                          </button>
                        );
                        if (currentPage > 4) {
                          pages.push(
                            <span key="ellipsis1" className="px-2 text-gray-500">
                              ...
                            </span>
                          );
                        }
                      }
                      
                      // Show pages around current page
                      for (let i = Math.max(1, currentPage - 2); i <= Math.min(totalPages, currentPage + 2); i++) {
                        pages.push(
                          <button
                            key={i}
                            onClick={() => handlePageChange(i)}
                            className={`px-3 py-1 text-sm border rounded-md ${
                              i === currentPage
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'bg-white border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {i}
                          </button>
                        );
                      }
                      
                      // Always show last page
                      if (currentPage < totalPages - 2) {
                        if (currentPage < totalPages - 3) {
                          pages.push(
                            <span key="ellipsis2" className="px-2 text-gray-500">
                              ...
                            </span>
                          );
                        }
                        pages.push(
                          <button
                            key={totalPages}
                            onClick={() => handlePageChange(totalPages)}
                            className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                          >
                            {totalPages}
                          </button>
                        );
                      }
                      
                      return pages;
                    })()}
                  </div>
                  
                  <button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page >= pagination.totalPages}
                    className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Sau
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <CreateClassModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateClassSubmit}
        isSubmitting={isCreating}
      />

      <EditClassModal
        classItem={editingClass}
        onClose={() => setEditingClass(null)}
      />

      <ImportClassModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImportSuccess={handleImportSuccess}
      />
    </div>
  );
};

export default ClassDashboard;
